// Emailing a signing link to an external party. One token per send, scoped to
// the party's role, single packet; the email is in the packet's language.
// Shared by the request wizard (send on create) and the request page (resend).

import { supabase } from '../supabase';
import { t, fill } from './i18n';
import { CRM_EMAIL } from '../org';

// A link for one party to sign one request, recorded against the address it is
// going to. Used on its own when the link rides in another email — the family's
// GAPP decision email carries the LON's link instead of a second email.
export async function createSigningLink({ packet, role, email, recipientName }) {
  const to = String(email || '').trim().toLowerCase();
  if (!to) throw new Error('Enter an email address first.');
  const row = { client_id: packet.client_id || null, packet_id: packet.id, party_role: role, sent_to_email: to, sent_to_name: recipientName || null };
  const { data, error } = await supabase.from('parent_portal_tokens').insert(row).select('token').single();
  if (error) throw error;
  return { url: `${window.location.origin}/sign/${data.token}`, to };
}

export async function sendSigningLink({ packet, client, role, email, lang, message, recipientName }) {
  const { url, to } = await createSigningLink({ packet, role, email, recipientName });
  const patient = packet.client_id ? `${client?.first_name || ''} ${client?.last_name || ''}`.trim() : (packet.subject || packet.title || 'documents');
  const { data: { session } } = await supabase.auth.getSession();
  const E = t(lang).email;
  const note = String(message || '').trim();
  const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify({
      to, cc: CRM_EMAIL,
      subject: fill(E.subject, { patient }),
      html: `
        <div lang="${lang}" style="font-family:'Trebuchet MS',sans-serif;max-width:600px;margin:0 auto;background:#EAF0F8;padding:24px;">
          <div style="background:#0A1628;border-radius:10px 10px 0 0;padding:20px 28px;">
            <div style="color:#fff;font-family:Georgia,serif;font-size:18px;font-weight:700;">${E.headerTitle}</div>
            <div style="color:#B9CBE0;font-size:12px;margin-top:4px;">${E.headerSub}</div>
          </div>
          <div style="background:#fff;border:1px solid #B9CBE0;border-top:none;border-radius:0 0 10px 10px;padding:28px;">
            <p style="color:#0A1628;font-size:15px;font-weight:700;margin:0 0 12px;">${E.greeting}</p>
            <p style="color:#333;font-size:13px;line-height:1.7;margin:0 0 16px;">${fill(E.body, { patient })}</p>
            ${note ? `<p style="color:#333;font-size:13px;line-height:1.7;margin:0 0 16px;border-left:3px solid #B9CBE0;padding-left:12px;white-space:pre-wrap;">${escapeHtml(note)}</p>` : ''}
            <div style="text-align:center;margin:28px 0;">
              <a href="${url}" style="background:#0DA06A;color:#fff;text-decoration:none;padding:14px 32px;border-radius:8px;font-weight:700;font-size:14px;display:inline-block;">${E.button}</a>
            </div>
            <p style="color:#aaa;font-size:11px;margin:0;">${E.fallback}<br/><span style="color:#1A3A6B;word-break:break-all;">${url}</span></p>
          </div>
          <p style="text-align:center;color:#aaa;font-size:10px;margin-top:14px;">${E.footer}</p>
        </div>`,
    }),
  });
  if (!res.ok) throw new Error(`Email send failed (${res.status})`);
  return { url, to };
}

const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
